import * as i0 from '@angular/core';
import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject, of, combineLatest, merge } from 'rxjs';
import { DataSource } from '@angular/cdk/collections';
import { map, filter, tap, switchMap, catchError, takeUntil, first, withLatestFrom, startWith } from 'rxjs/operators';
import * as i1 from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { __rest } from 'tslib';
import { add } from 'date-fns';

/**
 * Intended to be used when a form submit requires an OTP verification.
 * Can subscribe to success$ to check if the form submition went through
 * As the respone gets intercepted by the http interceptor.
 * @example
 * User wants to update a banking information.
 * OTP verification gets triggered.
 * Use this service to get notified if the otp verification and form submition went through
 */
class InterceptorResponseService {
    constructor() {
        this._success = new Subject();
        this.success$ = this._success.asObservable();
    }
    success() {
        this._success.next(true);
    }
}
InterceptorResponseService.ɵprov = i0.ɵɵdefineInjectable({ factory: function InterceptorResponseService_Factory() { return new InterceptorResponseService(); }, token: InterceptorResponseService, providedIn: "root" });
InterceptorResponseService.decorators = [
    { type: Injectable, args: [{
                providedIn: 'root',
            },] }
];

var HttpMethod;
(function (HttpMethod) {
    HttpMethod["DELETE"] = "DELETE";
    HttpMethod["GET"] = "GET";
    HttpMethod["POST"] = "POST";
    HttpMethod["PUT"] = "PUT";
})(HttpMethod || (HttpMethod = {}));
function isURL(maybeURL) {
    try {
        new URL(maybeURL);
        return true;
    }
    catch (_a) {
        return false;
    }
}
function urlJoin(...parts) {
    const filtered = parts.filter((part) => !!part && typeof part == 'string' && part.length > 0);
    const base = isURL(filtered[0]) ? filtered.shift() : '';
    return filtered
        .map((part) => part.replace(/^[\/\\]+|[\/\\]+$/g, ''))
        .reduce((url, part) => url.concat('/'.concat(part)), base);
}
class BaseHttpService {
    constructor(http) {
        this.http = http;
        this._baseUrl = '';
        this._DEFAULT_OPTIONS = { withCredentials: true };
    }
    set baseUrl(baseUrl) {
        this._baseUrl = baseUrl;
    }
    get baseUrl() {
        return this._baseUrl;
    }
    fetchWithFqn({ endpoint, httpMethod, body, responseField, options, }) {
        return this.buildRequest(endpoint, httpMethod, body, options).pipe(map((response) => responseField && response ? response[responseField] : response));
    }
    fetch({ endpoint, httpMethod, body, responseField, options, }) {
        return this.buildRequest(urlJoin(this._baseUrl, endpoint), httpMethod, body, options).pipe(map((response) => responseField && response ? response[responseField] : response));
    }
    buildRequest(endpoint, httpMethod, body, options) {
        switch (httpMethod) {
            case HttpMethod.POST:
                return this.http.post(endpoint, body, Object.assign(Object.assign({}, this._DEFAULT_OPTIONS), options));
            case HttpMethod.PUT:
                return this.http.put(endpoint, body, Object.assign(Object.assign({}, this._DEFAULT_OPTIONS), options));
            case HttpMethod.DELETE:
                return this.http.delete(endpoint, Object.assign(Object.assign({}, this._DEFAULT_OPTIONS), options));
            case HttpMethod.GET:
            default:
                return this.http.get(endpoint, Object.assign(Object.assign({}, this._DEFAULT_OPTIONS), options));
        }
    }
}
BaseHttpService.ɵprov = i0.ɵɵdefineInjectable({ factory: function BaseHttpService_Factory() { return new BaseHttpService(i0.ɵɵinject(i1.HttpClient)); }, token: BaseHttpService, providedIn: "root" });
BaseHttpService.decorators = [
    { type: Injectable, args: [{
                providedIn: 'root',
            },] }
];
BaseHttpService.ctorParameters = () => [
    { type: HttpClient }
];

var FilterType$2;
(function (FilterType) {
    FilterType["TEXT"] = "text";
    FilterType["MULTI_SELECT"] = "multiSelect";
    FilterType["DATE_TIME_RANGE"] = "dateTimeRange";
    FilterType["DATE_RANGE"] = "dateRange";
    FilterType["CURRENCY_RANGE"] = "currencyRange";
    FilterType["NUMERIC_RANGE"] = "numericRange";
    FilterType["SINGLE_SELECT"] = "singleSelect";
})(FilterType$2 || (FilterType$2 = {}));
var FieldType$2;
(function (FieldType) {
    FieldType["TEXT"] = "text";
    FieldType["CHECK_BOX_SELECTION"] = "checkboxSelection";
    FieldType["CALENDAR"] = "calendar";
    FieldType["INPUT_NUMBER"] = "inputNumber";
})(FieldType$2 || (FieldType$2 = {}));
var FieldSetLabel$2;
(function (FieldSetLabel) {
    FieldSetLabel["DATE"] = "From | To";
    FieldSetLabel["NUMERIC"] = "Min - Max";
})(FieldSetLabel$2 || (FieldSetLabel$2 = {}));
// TODO add all types from FilterType
const FIELD_TYPE_MAP$2 = {
    [FilterType$2.TEXT]: FieldType$2.TEXT,
    [FilterType$2.MULTI_SELECT]: FieldType$2.CHECK_BOX_SELECTION,
    [FilterType$2.DATE_TIME_RANGE]: FieldType$2.CALENDAR,
    [FilterType$2.DATE_RANGE]: FieldType$2.CALENDAR,
    [FilterType$2.CURRENCY_RANGE]: FieldType$2.INPUT_NUMBER,
    [FilterType$2.NUMERIC_RANGE]: FieldType$2.INPUT_NUMBER,
};
const FIELD_COMPONENT_PROPERTIES_MAP$2 = {
    [FieldType$2.CALENDAR]: {
        showIcon: true,
        appendTo: 'body',
    },
};
function mapFieldsDefinition$2() {
    return (source) => source.pipe(map((filters) => {
        return Object.entries(filters).reduce((fieldDefinitions, [fieldKey, filter]) => {
            var _a, _b;
            const _c = filter, _d = filter.type, _ = _c[_d], rest = __rest(_c, [typeof _d === "symbol" ? _d : _d + ""]);
            const type = FIELD_TYPE_MAP$2[filter.type];
            const fieldSet = (filter === null || filter === void 0 ? void 0 : filter.label) || '';
            const componentProperties = FIELD_COMPONENT_PROPERTIES_MAP$2[type];
            let fields = {};
            const field = Object.assign(Object.assign({}, rest), { fieldSet,
                type,
                componentProperties });
            if (filter === null || filter === void 0 ? void 0 : filter.type.endsWith('Range')) {
                const fieldSetLabel = (filter === null || filter === void 0 ? void 0 : filter.type.startsWith('date'))
                    ? FieldSetLabel$2.DATE
                    : FieldSetLabel$2.NUMERIC;
                fields = {
                    [`${fieldKey}Min`]: Object.assign(Object.assign({}, field), { defaultValue: (_a = filter[filter.type]) === null || _a === void 0 ? void 0 : _a.min, fieldSetLabel }),
                    [`${fieldKey}Max`]: Object.assign(Object.assign({}, field), { defaultValue: (_b = filter[filter.type]) === null || _b === void 0 ? void 0 : _b.max, fieldSetLabel }),
                };
            }
            else {
                fields = {
                    [fieldKey]: Object.assign(field, _ && { options: _ === null || _ === void 0 ? void 0 : _.options }),
                };
            }
            return Object.assign(Object.assign({}, fieldDefinitions), fields);
        }, {});
    }));
}

var LoadingState$2;
(function (LoadingState) {
    LoadingState[LoadingState["ERROR"] = -1] = "ERROR";
    LoadingState[LoadingState["READY"] = 0] = "READY";
    LoadingState[LoadingState["LOADING"] = 1] = "LOADING";
    LoadingState[LoadingState["DONE"] = 2] = "DONE";
})(LoadingState$2 || (LoadingState$2 = {}));
var FiltersState$2;
(function (FiltersState) {
    FiltersState[FiltersState["CLEARED"] = 0] = "CLEARED";
    FiltersState[FiltersState["APPLIED"] = 1] = "APPLIED";
})(FiltersState$2 || (FiltersState$2 = {}));

class SearchService extends DataSource {
    constructor(http) {
        super();
        this.http = http;
        this.configUrl$ = new BehaviorSubject('');
        this.dataUrl$ = new BehaviorSubject('');
        this.idField = null;
        this.status$ = new BehaviorSubject(LoadingState$2.READY);
        this.dataStatus$ = new BehaviorSubject(LoadingState$2.READY);
        this._onDestroy = new Subject();
        this.filters$ = new BehaviorSubject(null);
        this.fieldsDefinition = this.filters$.asObservable().pipe(filter((x) => !!x), mapFieldsDefinition$2());
        this.searchFilters$ = new BehaviorSubject({});
        this.simpleSearch$ = new BehaviorSubject('');
        this.toolbarButtons$ = new BehaviorSubject({});
        this.columns$ = new BehaviorSubject({});
        this.defaultSort$ = new BehaviorSubject(null);
        this.sort$ = new BehaviorSubject(null);
        this.totalRecords$ = new BehaviorSubject(0);
        this.programmaticPaging$ = new Subject();
        this.paging$ = new BehaviorSubject({
            limit: 25,
            offset: 0,
        });
        this.data$ = new BehaviorSubject([]);
        this.selection$ = new BehaviorSubject([]);
        this.columnWidth$ = new BehaviorSubject({});
        this.columnsToDisplay$ = new BehaviorSubject([]);
        const url = this.configUrl$.asObservable().pipe(tap(() => this.status$.next(LoadingState$2.READY)), filter((url) => !!url), tap(() => this.status$.next(LoadingState$2.LOADING)), switchMap((url) => this.http
            .fetchWithFqn({
            endpoint: url,
            httpMethod: HttpMethod.GET,
        })
            .pipe(catchError((err) => {
            this.status$.next(LoadingState$2.ERROR);
            return of(err);
        }))
        // mockConfig
        ), tap(({ columns, defaultSort, filters, toolbarButtons, }) => {
            columns && this.columns$.next(columns);
            defaultSort && this.defaultSort$.next(defaultSort);
            filters && this.filters$.next(filters);
            toolbarButtons && this.toolbarButtons$.next(toolbarButtons);
        }), tap(() => this.status$.value !== LoadingState$2.ERROR &&
            this.status$.next(LoadingState$2.DONE)));
        const programmaticPaging = this.programmaticPaging$
            .asObservable()
            .pipe(tap((partialPaging) => this.paging$.next(Object.assign(Object.assign({}, this.paging$.value), partialPaging))));
        const simplePageReset = this.simpleSearch$
            .asObservable()
            .pipe(tap(() => this.programmaticPaging$.next({ offset: 0 })));
        const data = combineLatest([
            this.searchFilters$,
            this.sort$,
            this.defaultSort$,
            this.paging$,
            this.simpleSearch$,
        ]).pipe(filter(() => !!this.dataUrl$.value), tap(() => this.dataStatus$.next(LoadingState$2.LOADING)), switchMap(([filters, sort, defaultSort, page, simpleSearch]) => this.http
            .fetchWithFqn({
            httpMethod: HttpMethod.POST,
            endpoint: this.dataUrl$.value,
            body: Object.assign({
                page,
                search: { filters, term: simpleSearch },
            }, sort ? { sort } : { sort: Object.assign({}, defaultSort) }),
        })
            .pipe(catchError((err) => {
            this.dataStatus$.next(LoadingState$2.ERROR);
            return of(err);
        }))
        // mockData
        ), 
        // map(() => ({ records: [], totalRecords: 0 })),
        tap((records) => {
            var _a;
            this.data$.next((records === null || records === void 0 ? void 0 : records.records) || []);
            this.totalRecords$.next((_a = records === null || records === void 0 ? void 0 : records.totalRecords) !== null && _a !== void 0 ? _a : 0);
        }), tap((records) => {
            this.selection$.next([]);
        }), tap(() => this.dataStatus$.value !== LoadingState$2.ERROR &&
            this.dataStatus$.next(LoadingState$2.DONE)));
        const columnsToDisplay = this.columns$.pipe(filter((columns) => !!columns), map((columns) => Object.entries(columns).reduce((toDisplay, [key, { isDefault }]) => (isDefault ? [...toDisplay, key] : [...toDisplay]), [])), map((columns) => this.idField ? ['select', ...columns, 'fill'] : [...columns, 'fill']), tap((columns) => this.columnsToDisplay$.next(columns)));
        merge(url, data, programmaticPaging, simplePageReset, columnsToDisplay)
            .pipe(takeUntil(this._onDestroy))
            .subscribe();
    }
    set configUrl(endpoint) {
        this.configUrl$.next(endpoint);
    }
    get configUrl() {
        return this.configUrl$.value;
    }
    set dataUrl(endpoint) {
        this.dataUrl$.next(endpoint);
    }
    get dataUrl() {
        return this.dataUrl$.value;
    }
    get status() {
        return this.status$.value;
    }
    get dataStatus() {
        return this.dataStatus$.value;
    }
    get columnsToDisplay() {
        return this.columnsToDisplay$.value;
    }
    get columnWidth() {
        return this.columnWidth$.value;
    }
    set paginator(paginator) {
        if (paginator) {
            this._paginator = paginator;
            this._setupPaginator();
        }
    }
    updateColumnToDisplay(columns) {
        if (!columns.includes('select') && this.idField) {
            columns = ['select', ...columns];
        }
        if (!columns.includes('fill')) {
            columns = [...columns, 'fill'];
        }
        this.columnsToDisplay$.next(columns);
        // this._updateStateStorage('local', {
        //   columnsToDisplay: columns.filter((a) => a !== 'select' && a !== 'fill'),
        // });
    }
    isAllSelected() {
        return this.selection$.value.length === this.data$.value.length;
    }
    toggleAllRows() {
        this.isAllSelected()
            ? this.selection$.next([])
            : this.selection$.next(this.data$.value.map((row) => row[this.idField]));
    }
    select(id) {
        this.selection$.value.includes(id)
            ? this.selection$.next([
                ...this.selection$.value.reduce((list, currentId) => id === currentId ? [...list] : [...list, currentId], []),
            ])
            : this.selection$.next([...this.selection$.value, id]);
    }
    updateSort(sort, field) {
        const direction = sort === null || sort === void 0 ? void 0 : sort.direction;
        this.sort$.next(!!direction ? { field, order: direction } : {});
    }
    updateColumnWidth(width, field) {
        this.columnWidth$.next(Object.assign(Object.assign({}, this.columnWidth$.value), { [field]: width }));
    }
    reset() {
        this.configUrl$.next('');
    }
    connect() {
        return this.data$;
    }
    disconnect() { }
    ngOnDestroy() {
        this._onDestroy.next(true);
    }
    _setupPaginator() {
        var _a;
        (_a = this._paginatorSubscriptions) === null || _a === void 0 ? void 0 : _a.unsubscribe();
        const pageChange = this._paginator
            ? merge(this._paginator.page, this._paginator.initialized)
            : of(null);
        const totalRecords = this.totalRecords$.pipe(tap((records) => {
            this._paginator.length = records;
        }));
        const getPaginator = this.paging$.pipe(tap((page) => {
            // this._updateStateStorage('local', { pageSize: page.limit });
            // this._updateStateStorage('session', { offset: page.offset });
            this._paginator.pageIndex = page.offset / page.limit;
            this._paginator.pageSize = page.limit;
        }));
        const setPaginator = pageChange.pipe(tap((page) => {
            if (page) {
                this.paging$.next({
                    limit: page.pageSize,
                    offset: page.pageIndex * page.pageSize,
                });
            }
        }));
        // const setSession = this._sessionStorageConfigs.pipe(
        //   tap((a) => {
        //     // this._setStateStorage('session', a);
        //   })
        // );
        // const setLocal = this._localStorageConfigs.pipe(
        //   tap((a) => {
        //     // this._setStateStorage('local', a);
        //   })
        // );
        this._paginatorSubscriptions = merge(getPaginator, setPaginator, totalRecords)
            .pipe(takeUntil(this._onDestroy))
            .subscribe();
    }
}
SearchService.ɵprov = i0.ɵɵdefineInjectable({ factory: function SearchService_Factory() { return new SearchService(i0.ɵɵinject(BaseHttpService)); }, token: SearchService, providedIn: "root" });
SearchService.decorators = [
    { type: Injectable, args: [{
                providedIn: 'root',
            },] }
];
SearchService.ctorParameters = () => [
    { type: BaseHttpService }
];

const filterer$1 = ([data, body, fields]) => {
    if ((body === null || body === void 0 ? void 0 : body.search) === undefined) {
        return [data, body];
    }
    const includedFilteredRow = filterPredicates$1(body.search.filters);
    const filteredWithEvery = getFilterFunction$1(includedFilteredRow);
    // function call that turns term into filter object
    // filterPredicates call passed in with the converted term to filter object
    // creating the term
    const termAsFilter = convertTermToFilter$1(body.search.term, fields);
    const includedTermRow = filterPredicates$1(termAsFilter);
    const filteredWithSome = getFilterFunction$1(includedTermRow, true);
    const newData = data.reduce((filteredData, row) => {
        return filteredWithEvery(row) && filteredWithSome(row)
            ? [...filteredData, row]
            : filteredData;
    }, []);
    return [newData, body];
};
const convertTermToFilter$1 = (term, fields) => {
    const filterObject = fields.reduce((filteredObject, searchTerm) => (Object.assign(Object.assign({}, filteredObject), { [searchTerm]: { fz: [term.toLowerCase()] } })), {});
    return filterObject;
};
const ɵ0$1 = convertTermToFilter$1;
const filterPredicates$1 = (filterObject) => {
    // this is getting all of the filter objects inside of the payload
    const filters = filterObject;
    if (Object.keys(filters).length === 0) {
        return [(_) => true];
    }
    return Object.keys(filters).map((field) => {
        // this will contain the filter object with the operator as its key and its value
        const filter = filters[field];
        // note that these could be potential issues as there could be more than one argument in the filter object
        // getting the operator from the filter
        const [operator] = Object.keys(filter);
        const [argument] = filter[operator];
        switch (operator) {
            case 'eq':
                return ({ [field]: value }) => {
                    return value === argument;
                };
            case 'fz':
                return ({ [field]: value }) => {
                    return value.toLowerCase().includes(argument.toLowerCase());
                };
            case 'gt':
                return ({ [field]: value }) => {
                    return value > argument;
                };
            case 'gte':
                return ({ [field]: value }) => {
                    return value >= argument;
                };
            case 'lt':
                return ({ [field]: value }) => {
                    return value < argument;
                };
            case 'lte':
                return ({ [field]: value }) => {
                    return value <= argument;
                };
            default:
                return (_) => true;
        }
    });
};
const getFilterFunction$1 = (
// tslint:disable-next-line: array-type
shouldThisRowBeIncluded, doSome = false) => {
    if (doSome) {
        return (row) => shouldThisRowBeIncluded.some((filter) => {
            return filter(row);
        });
    }
    else {
        return (row) => shouldThisRowBeIncluded.every((filter) => {
            return filter(row);
        });
    }
};
const ɵ1$1 = getFilterFunction$1;
const paginator$1 = ([data, body]) => {
    if ((body === null || body === void 0 ? void 0 : body.page) === undefined) {
        return { records: data, totalRecords: data.length, errorCode: 0 };
    }
    const splitData = data.slice(body.page.offset, body.page.offset + body.page.limit);
    return { records: splitData, totalRecords: data.length, errorCode: 0 };
};
const sorter$1 = ([data, body]) => {
    if ((body === null || body === void 0 ? void 0 : body.sort) === undefined) {
        return [data, body];
    }
    // ascending sort
    // check if the property ends with formatted - and then
    const formattedRegExp = new RegExp('Formatted$');
    const sorted = [
        ...data.sort((a, b) => {
            // string sort
            const field = body.sort.field.split(formattedRegExp)[0];
            if (typeof a[field] === 'string') {
                return body.sort.order === -1
                    ? b[field].toLowerCase().localeCompare(a[field].toLowerCase())
                    : a[field].toLowerCase().localeCompare(b[field].toLowerCase());
            }
            // number sort
            return body.sort.order === -1 ? b[field] - a[field] : a[field] - b[field];
        }),
    ];
    return [sorted, body];
};

var LoadingState$1;
(function (LoadingState) {
    LoadingState[LoadingState["ERROR"] = -1] = "ERROR";
    LoadingState[LoadingState["READY"] = 0] = "READY";
    LoadingState[LoadingState["LOADING"] = 1] = "LOADING";
    LoadingState[LoadingState["DONE"] = 2] = "DONE";
})(LoadingState$1 || (LoadingState$1 = {}));
var FiltersState$1;
(function (FiltersState) {
    FiltersState[FiltersState["CLEARED"] = 0] = "CLEARED";
    FiltersState[FiltersState["APPLIED"] = 1] = "APPLIED";
})(FiltersState$1 || (FiltersState$1 = {}));
var EditMenuButtonType$1;
(function (EditMenuButtonType) {
    EditMenuButtonType["DEFAULT"] = "default";
    EditMenuButtonType["NEW"] = "new";
    EditMenuButtonType["DELETE"] = "delete";
    EditMenuButtonType["POPUP"] = "popup";
})(EditMenuButtonType$1 || (EditMenuButtonType$1 = {}));

var FilterType$1;
(function (FilterType) {
    FilterType["TEXT"] = "text";
    FilterType["MULTI_SELECT"] = "multiSelect";
    FilterType["DATE_TIME_RANGE"] = "dateTimeRange";
    FilterType["DATE_RANGE"] = "dateRange";
    FilterType["CURRENCY_RANGE"] = "currencyRange";
    FilterType["NUMERIC_RANGE"] = "numericRange";
    FilterType["SINGLE_SELECT"] = "singleSelect";
})(FilterType$1 || (FilterType$1 = {}));
var FieldType$1;
(function (FieldType) {
    FieldType["TEXT"] = "text";
    FieldType["CHECK_BOX_SELECTION"] = "checkboxSelection";
    FieldType["CALENDAR"] = "calendar";
    FieldType["INPUT_NUMBER"] = "inputNumber";
})(FieldType$1 || (FieldType$1 = {}));
var FieldSetLabel$1;
(function (FieldSetLabel) {
    FieldSetLabel["DATE"] = "From | To";
    FieldSetLabel["NUMERIC"] = "Min - Max";
})(FieldSetLabel$1 || (FieldSetLabel$1 = {}));
// TODO add all types from FilterType
const FIELD_TYPE_MAP$1 = {
    [FilterType$1.TEXT]: FieldType$1.TEXT,
    [FilterType$1.MULTI_SELECT]: FieldType$1.CHECK_BOX_SELECTION,
    [FilterType$1.DATE_TIME_RANGE]: FieldType$1.CALENDAR,
    [FilterType$1.DATE_RANGE]: FieldType$1.CALENDAR,
    [FilterType$1.CURRENCY_RANGE]: FieldType$1.INPUT_NUMBER,
    [FilterType$1.NUMERIC_RANGE]: FieldType$1.INPUT_NUMBER,
};
const FIELD_COMPONENT_PROPERTIES_MAP$1 = {
    [FieldType$1.CALENDAR]: {
        showIcon: true,
        appendTo: 'body',
    },
};
function mapFieldsDefinition$1() {
    return (source) => source.pipe(map((filters) => {
        return Object.entries(filters).reduce((fieldDefinitions, [fieldKey, filter]) => {
            var _a, _b;
            const _c = filter, _d = filter.type, _ = _c[_d], rest = __rest(_c, [typeof _d === "symbol" ? _d : _d + ""]);
            const type = FIELD_TYPE_MAP$1[filter.type];
            const fieldSet = (filter === null || filter === void 0 ? void 0 : filter.label) || '';
            const componentProperties = FIELD_COMPONENT_PROPERTIES_MAP$1[type];
            let fields = {};
            const field = Object.assign(Object.assign({}, rest), { fieldSet,
                type,
                componentProperties });
            if (filter === null || filter === void 0 ? void 0 : filter.type.endsWith('Range')) {
                const fieldSetLabel = (filter === null || filter === void 0 ? void 0 : filter.type.startsWith('date'))
                    ? FieldSetLabel$1.DATE
                    : FieldSetLabel$1.NUMERIC;
                fields = {
                    [`${fieldKey}Min`]: Object.assign(Object.assign({}, field), { defaultValue: (_a = filter[filter.type]) === null || _a === void 0 ? void 0 : _a.min, fieldSetLabel }),
                    [`${fieldKey}Max`]: Object.assign(Object.assign({}, field), { defaultValue: (_b = filter[filter.type]) === null || _b === void 0 ? void 0 : _b.max, fieldSetLabel }),
                };
            }
            else {
                fields = {
                    [fieldKey]: Object.assign(field, _ && { options: _ === null || _ === void 0 ? void 0 : _.options }),
                };
            }
            return Object.assign(Object.assign({}, fieldDefinitions), fields);
        }, {});
    }));
}
const MIN$1 = 'Min';
const MAX$1 = 'Max';
function mapFilters$1(formValues, fields) {
    const appliedFilters = Object.keys(fields || {}).reduce((filters, field) => {
        return Object.assign(Object.assign({}, filters), getFilterByFieldType$1(formValues, fields, field));
    }, {});
    return appliedFilters;
}
function getFilterByFieldType$1(formValues, fields, field) {
    const config = fields[field];
    switch (config.type) {
        case FieldType$1.CHECK_BOX_SELECTION:
            return eq$1(field, formValues[field]);
        case FieldType$1.CALENDAR:
            return dateRange$1(field, field.endsWith(MIN$1)
                ? formValues[field]
                : formValues[field.replace(MAX$1, MIN$1)], field.endsWith(MAX$1)
                ? formValues[field]
                : formValues[field.replace(MIN$1, MAX$1)]);
        case FieldType$1.INPUT_NUMBER:
            return range$1(field, field.endsWith(MIN$1)
                ? formValues[field]
                : formValues[field.replace(MAX$1, MIN$1)], field.endsWith(MAX$1)
                ? formValues[field]
                : formValues[field.replace(MIN$1, MAX$1)]);
        case 'radio':
            return eq$1(field, formValues[field]);
        default:
            return fz$1(field, formValues[field]);
    }
}
function fz$1(facetName, value = '') {
    return value === '' ? {} : { [facetName]: { fz: [value] } };
}
function dateRange$1(facetName, min, max) {
    const body = Object.assign({}, min && { gte: [min.toISOString()] }, max && { lt: [add(max, { days: 1 }).toISOString()] });
    return min || max
        ? {
            [facetName.slice(0, facetName.length - 3)]: body,
        }
        : {};
}
function range$1(facetName, min, max) {
    const hasMin = typeof min === 'number';
    const hasMax = typeof max === 'number';
    const body = Object.assign({}, hasMin && { gte: [`${min}`] }, hasMax && { lte: [`${max}`] });
    return hasMin || hasMax
        ? {
            [facetName.slice(0, facetName.length - 3)]: body,
        }
        : {};
}
function eq$1(facetName, values = []) {
    return values.length === 0 ? {} : { [facetName]: { eq: values } };
}
const schemaDefinition$1 = (fields) => ({
    fields,
});
const addPartialFields$1 = (column, data) => {
    return Object.assign(Object.assign({}, column), { fields: Object.assign({}, Object.entries(column.fields).reduce((totalFields, [key, field]) => {
            var _a, _b;
            return (data[key] !== null && data[key] !== undefined
                ? ((_a = data[key]) === null || _a === void 0 ? void 0 : _a.constructor) === Object
                : false)
                ? Object.assign(Object.assign({}, totalFields), { [key]: Object.assign({ readonly: false }, Object.assign({}, field), Object.assign({}, data[key]), {
                        componentProperties: Object.assign(Object.assign({}, ((field === null || field === void 0 ? void 0 : field.componentProperties) || {})), (data[key]
                            ? ((_b = data[key]) === null || _b === void 0 ? void 0 : _b.componentProperties) || {}
                            : {})),
                    }) }) : Object.assign(Object.assign({}, totalFields), { [key]: Object.assign({ readonly: true }, field) });
        }, {})) });
};
const getDefaults$1 = (data) => {
    return Object.entries(data).reduce((total, [key, value]) => {
        return (value !== null && value !== undefined
            ? value.constructor === Object
            : false)
            ? Object.assign(Object.assign({}, total), (value === null || value === void 0 ? void 0 : value.defaultValue) && { [key]: value.defaultValue })
            : Object.assign(Object.assign({}, total), { [key]: value });
    }, {});
};
var TableAlignment$1;
(function (TableAlignment) {
    TableAlignment["LEFT"] = "left";
    TableAlignment["CENTER"] = "center";
    TableAlignment["RIGHT"] = "right";
})(TableAlignment$1 || (TableAlignment$1 = {}));

class EditLocalSearchService {
    constructor() {
        this.url$ = new BehaviorSubject('');
        this.status$ = new BehaviorSubject(LoadingState$1.READY);
        this._onDestroy = new Subject();
        this.filters$ = new BehaviorSubject(null);
        this.fieldsDefinition = this.filters$.asObservable().pipe(filter((x) => !!x), mapFieldsDefinition$1());
        this.searchFilters$ = new BehaviorSubject({});
        this.toolbarButtons$ = new BehaviorSubject(null);
        this.columns$ = new BehaviorSubject(null);
        this.defaultSort$ = new BehaviorSubject(null);
        this.sort$ = new BehaviorSubject(null);
        this.totalRecords$ = new BehaviorSubject(0);
        this.paging$ = new BehaviorSubject({
            limit: 9999,
            offset: 0,
        });
        this.allData$ = new BehaviorSubject([]);
        this.data$ = new BehaviorSubject([]);
        this.fields$ = new BehaviorSubject([]);
        this.reSearch$ = new Subject();
        combineLatest([
            this.allData$,
            this.searchFilters$,
            this.sort$,
            this.defaultSort$,
            this.paging$,
            this.fields$,
        ])
            .pipe(tap(() => this.status$.next(LoadingState$1.LOADING)), map(([allData, searchFilters, sort, defaultSort, paging, fields]) => {
            return filterer$1([
                allData,
                {
                    page: paging,
                    search: { filters: searchFilters, term: '' },
                    sort: sort
                        ? Object.keys(sort).length > 0
                            ? sort
                            : defaultSort
                        : defaultSort,
                },
                fields,
            ]);
        }), map(sorter$1), map(paginator$1), tap((records) => {
            this.data$.next((records === null || records === void 0 ? void 0 : records.records) || []);
            this.totalRecords$.next(records === null || records === void 0 ? void 0 : records.totalRecords);
        }), tap(() => this.status$.next(LoadingState$1.DONE)), takeUntil(this._onDestroy))
            .subscribe();
    }
    set url(endpoint) {
        this.url$.next(endpoint);
    }
    get url() {
        return this.url$.value;
    }
    get status() {
        return this.status$.value;
    }
    reset() {
        this.url$.next('');
    }
    ngOnDestroy() {
        this._onDestroy.next(true);
    }
}
EditLocalSearchService.ɵprov = i0.ɵɵdefineInjectable({ factory: function EditLocalSearchService_Factory() { return new EditLocalSearchService(); }, token: EditLocalSearchService, providedIn: "root" });
EditLocalSearchService.decorators = [
    { type: Injectable, args: [{
                providedIn: 'root',
            },] }
];
EditLocalSearchService.ctorParameters = () => [];

const filterer = ([data, body, fields]) => {
    var _a;
    if (!(body === null || body === void 0 ? void 0 : body.search) || !fields) {
        return [data, body, []];
    }
    const includedFilteredRow = filterPredicates(body.search.filters);
    const filteredWithEvery = getFilterFunction(includedFilteredRow);
    // function call that turns term into filter object
    // filterPredicates call passed in with the converted term to filter object
    // creating the term
    const termAsFilter = convertTermToFilter((_a = body.search.term) !== null && _a !== void 0 ? _a : '', fields);
    const includedTermRow = filterPredicates(termAsFilter);
    const filteredWithSome = getFilterFunction(includedTermRow, true);
    const newData = data.filter((row) => {
        return filteredWithEvery(row) && filteredWithSome(row);
    });
    const filteredData = data.filter((row) => {
        return !(filteredWithEvery(row) && filteredWithSome(row));
    });
    return [newData, body, filteredData];
};
const sorter = ([data, body, filteredData]) => {
    if (!(body === null || body === void 0 ? void 0 : body.sort)) {
        return [data, body, filteredData];
    }
    // ascending sort
    // check if the property ends with formatted - and then
    const formattedRegExp = new RegExp('Formatted$');
    const sorted = [
        ...data.sort((a, b) => {
            // string sort
            const field = body.sort.field.split(formattedRegExp)[0];
            if (typeof a[field] === 'string' || typeof b[field] === 'string') {
                return body.sort.order === -1
                    ? String(b[field])
                        .toLowerCase()
                        .localeCompare(String(a[field]).toLowerCase())
                    : String(a[field])
                        .toLowerCase()
                        .localeCompare(String(b[field]).toLowerCase());
            }
            // number sort
            return body.sort.order === -1 ? b[field] - a[field] : a[field] - b[field];
        }),
    ];
    return [sorted, body, filteredData];
};
const paginator = ([data, body, filteredData]) => {
    if (!(body === null || body === void 0 ? void 0 : body.page)) {
        return { records: data, totalRecords: data.length, errorCode: 0 };
    }
    const splitData = data.slice(body.page.offset, body.page.offset + body.page.limit);
    const offPageData = [
        ...data.slice(0, body.page.offset),
        ...data.slice(body.page.offset + body.page.limit, data.length),
        ...filteredData,
    ];
    return {
        records: splitData,
        totalRecords: data.length,
        offPageData: offPageData,
        errorCode: 0,
    };
};
var FilterType;
(function (FilterType) {
    FilterType["TEXT"] = "text";
    FilterType["MULTI_SELECT"] = "multiSelect";
    FilterType["DATE_TIME_RANGE"] = "dateTimeRange";
    FilterType["DATE_RANGE"] = "dateRange";
    FilterType["CURRENCY_RANGE"] = "currencyRange";
    FilterType["NUMERIC_RANGE"] = "numericRange";
    FilterType["SINGLE_SELECT"] = "singleSelect";
})(FilterType || (FilterType = {}));
var FieldType;
(function (FieldType) {
    FieldType["TEXT"] = "text";
    FieldType["CHECK_BOX_SELECTION"] = "checkboxSelection";
    FieldType["CALENDAR"] = "calendar";
    FieldType["INPUT_NUMBER"] = "inputNumber";
})(FieldType || (FieldType = {}));
var FieldSetLabel;
(function (FieldSetLabel) {
    FieldSetLabel["DATE"] = "From | To";
    FieldSetLabel["NUMERIC"] = "Min - Max";
})(FieldSetLabel || (FieldSetLabel = {}));
// TODO add all types from FilterType
const FIELD_TYPE_MAP = {
    [FilterType.TEXT]: FieldType.TEXT,
    [FilterType.MULTI_SELECT]: FieldType.CHECK_BOX_SELECTION,
    [FilterType.DATE_TIME_RANGE]: FieldType.CALENDAR,
    [FilterType.DATE_RANGE]: FieldType.CALENDAR,
    [FilterType.CURRENCY_RANGE]: FieldType.INPUT_NUMBER,
    [FilterType.NUMERIC_RANGE]: FieldType.INPUT_NUMBER,
};
const FIELD_COMPONENT_PROPERTIES_MAP = {
    [FieldType.CALENDAR]: {
        showIcon: true,
        appendTo: 'body',
    },
};
function mapFieldsDefinition() {
    return (source) => source.pipe(map(([filters, labels]) => {
        return Object.entries(filters).reduce((fieldDefinitions, [fieldKey, filter]) => {
            var _a, _b, _c, _d;
            const _e = filter, _f = filter.type, _ = _e[_f], rest = __rest(_e, [typeof _f === "symbol" ? _f : _f + ""]);
            const type = FIELD_TYPE_MAP[filter.type];
            const fieldSet = (filter === null || filter === void 0 ? void 0 : filter.label) || '';
            const componentProperties = FIELD_COMPONENT_PROPERTIES_MAP[type];
            let fields = {};
            const field = Object.assign(Object.assign({}, rest), { fieldSet,
                type,
                componentProperties });
            if (filter === null || filter === void 0 ? void 0 : filter.type.endsWith('Range')) {
                const fieldSetLabel = (filter === null || filter === void 0 ? void 0 : filter.type.startsWith('date'))
                    ? (_a = labels === null || labels === void 0 ? void 0 : labels.dateRangeLabel) !== null && _a !== void 0 ? _a : FieldSetLabel.DATE
                    : (_b = labels === null || labels === void 0 ? void 0 : labels.numericRangeLabel) !== null && _b !== void 0 ? _b : FieldSetLabel.NUMERIC;
                fields = {
                    [`${fieldKey}Min`]: Object.assign(Object.assign({}, field), { defaultValue: (_c = filter[filter.type]) === null || _c === void 0 ? void 0 : _c.min, fieldSetLabel }),
                    [`${fieldKey}Max`]: Object.assign(Object.assign({}, field), { defaultValue: (_d = filter[filter.type]) === null || _d === void 0 ? void 0 : _d.max, fieldSetLabel }),
                };
            }
            else {
                fields = {
                    [fieldKey]: Object.assign(field, _ && { options: _ === null || _ === void 0 ? void 0 : _.options }),
                };
            }
            return Object.assign(Object.assign({}, fieldDefinitions), fields);
        }, {});
    }));
}
const MIN = 'Min';
const MAX = 'Max';
function mapFilters(formValues, fields) {
    const appliedFilters = Object.keys(fields || {}).reduce((filters, field) => {
        return Object.assign(Object.assign({}, filters), getFilterByFieldType(formValues, fields, field));
    }, {});
    return appliedFilters;
}
function getFilterByFieldType(formValues, fields, field) {
    const config = fields[field];
    switch (config.type) {
        case FieldType.CHECK_BOX_SELECTION:
            return eq(field, formValues[field]);
        case FieldType.CALENDAR:
            return dateRange(field, field.endsWith(MIN)
                ? formValues[field]
                : formValues[field.replace(MAX, MIN)], field.endsWith(MAX)
                ? formValues[field]
                : formValues[field.replace(MIN, MAX)]);
        case FieldType.INPUT_NUMBER:
            return range(field, field.endsWith(MIN)
                ? formValues[field]
                : formValues[field.replace(MAX, MIN)], field.endsWith(MAX)
                ? formValues[field]
                : formValues[field.replace(MIN, MAX)]);
        case 'radio':
            return eq(field, formValues[field]);
        default:
            return fz(field, formValues[field]);
    }
}
function fz(facetName, value = '') {
    return value === '' || value === null || value === undefined
        ? {}
        : { [facetName]: { fz: [value] } };
}
function dateRange(facetName, min, max) {
    const body = Object.assign({}, min && { gte: [min.toISOString()] }, max && { lt: [add(max, { days: 1 }).toISOString()] });
    return min || max
        ? {
            [facetName.slice(0, facetName.length - 3)]: body,
        }
        : {};
}
function range(facetName, min, max) {
    const hasMin = typeof min === 'number';
    const hasMax = typeof max === 'number';
    const body = Object.assign({}, hasMin && { gte: [`${min}`] }, hasMax && { lte: [`${max}`] });
    return hasMin || hasMax
        ? {
            [facetName.slice(0, facetName.length - 3)]: body,
        }
        : {};
}
function eq(facetName, values = []) {
    return (values === null || values === void 0 ? void 0 : values.length) === 0 ||
        (values === null || values === void 0 ? void 0 : values.length) === null ||
        (values === null || values === void 0 ? void 0 : values.length) === undefined
        ? {}
        : { [facetName]: { eq: values } };
}
const schemaDefinition = (fields) => ({
    fields,
});
const addPartialFields = (column, data) => {
    return Object.assign(Object.assign({}, column), { fields: Object.assign({}, Object.entries(column.fields).reduce((totalFields, [key, field]) => {
            var _a, _b;
            return (data[key] !== null && data[key] !== undefined
                ? ((_a = data[key]) === null || _a === void 0 ? void 0 : _a.constructor) === Object
                : false)
                ? Object.assign(Object.assign({}, totalFields), { [key]: Object.assign({ readonly: false }, Object.assign({}, field), Object.assign({}, data[key]), {
                        componentProperties: Object.assign(Object.assign({}, ((field === null || field === void 0 ? void 0 : field.componentProperties) || {})), (data[key]
                            ? ((_b = data[key]) === null || _b === void 0 ? void 0 : _b.componentProperties) || {}
                            : {})),
                    }) }) : Object.assign(Object.assign({}, totalFields), { [key]: Object.assign({ readonly: true }, field) });
        }, {})) });
};
const getDefaults = (data) => {
    return Object.entries(data).reduce((total, [key, value]) => {
        return (value !== null && value !== undefined
            ? value.constructor === Object
            : false)
            ? !Object.keys(value).includes('phoneNumber')
                ? Object.assign(Object.assign({}, total), (value === null || value === void 0 ? void 0 : value.defaultValue) && { [key]: value.defaultValue })
                : Object.assign(Object.assign({}, total), { [key]: value })
            : Object.assign(Object.assign({}, total), { [key]: value });
    }, {});
};
const convertTermToFilter = (term, fields) => {
    const filterObject = fields.reduce((filteredObject, searchTerm) => (Object.assign(Object.assign({}, filteredObject), { [searchTerm]: { fz: [term.toLowerCase()] } })), {});
    return filterObject;
};
const ɵ0 = convertTermToFilter;
// convert the filter object into an array of callable predicates
const filterPredicates = (filters) => {
    if (Object.keys(filters).length === 0) {
        return [(_) => true];
    }
    return Object.keys(filters)
        .map((field) => {
        // Create a predicate array for each field in the filter
        const operations = filters[field];
        return Object.keys(operations).map((operator) => {
            const argumentList = operations[operator];
            // Note: using .some() with the argument list may not be
            // desirable in all cases, but it is compatible for now
            // since we were previously only using the first argument.
            switch (operator) {
                case 'eq':
                    return ({ [field]: value }) => {
                        return argumentList.some((arg) => value === arg);
                    };
                case 'fz':
                    return ({ [field]: value }) => {
                        return argumentList.some((arg) => String(value)
                            .toLowerCase()
                            .includes(String(arg).toLowerCase()));
                    };
                case 'gt':
                    return ({ [field]: value }) => {
                        return argumentList.some((arg) => value > arg);
                    };
                case 'gte':
                    return ({ [field]: value }) => {
                        return argumentList.some((arg) => value >= arg);
                    };
                case 'lt':
                    return ({ [field]: value }) => {
                        return argumentList.some((arg) => value < arg);
                    };
                case 'lte':
                    return ({ [field]: value }) => {
                        return argumentList.some((arg) => value <= arg);
                    };
                default:
                    return (_) => true;
            }
        });
    })
        .reduce((prev, curr) => {
        // we don't need or want a 2D array, so flatten the whole thing
        return prev.concat(curr);
    }, []);
};
const getFilterFunction = (
// tslint:disable-next-line: array-type
shouldThisRowBeIncluded, doSome = false) => {
    if (doSome) {
        return (row) => shouldThisRowBeIncluded.some((filter) => {
            return filter(row);
        });
    }
    else {
        return (row) => shouldThisRowBeIncluded.every((filter) => {
            return filter(row);
        });
    }
};
const ɵ1 = getFilterFunction;
const defaultSelectionColumn = {
    type: 'checkbox',
    defaultValue: false,
    readonly: false,
    options: [{ value: true, label: '' }],
};
const addSelectionColumn = (columns) => {
    const _a = (columns === null || columns === void 0 ? void 0 : columns.fields) || {}, { select } = _a, rest = __rest(_a, ["select"]);
    return Object.assign(Object.assign({}, columns), { fields: Object.assign({ select: Object.assign(Object.assign({}, defaultSelectionColumn), (select || {})) }, rest) });
};

var EditMenuButtonType;
(function (EditMenuButtonType) {
    EditMenuButtonType["DEFAULT"] = "default";
    EditMenuButtonType["NEW"] = "new";
    EditMenuButtonType["DELETE"] = "delete";
    EditMenuButtonType["POPUP"] = "popup";
    EditMenuButtonType["NAVIGATE"] = "navigate";
    EditMenuButtonType["CONFIRMATION"] = "confirmation";
})(EditMenuButtonType || (EditMenuButtonType = {}));
const formDefinition = (fields) => ({
    fields,
});
var LoadingState;
(function (LoadingState) {
    LoadingState[LoadingState["ERROR"] = -1] = "ERROR";
    LoadingState[LoadingState["READY"] = 0] = "READY";
    LoadingState[LoadingState["LOADING"] = 1] = "LOADING";
    LoadingState[LoadingState["DONE"] = 2] = "DONE";
})(LoadingState || (LoadingState = {}));
var FiltersState;
(function (FiltersState) {
    FiltersState[FiltersState["CLEARED"] = 0] = "CLEARED";
    FiltersState[FiltersState["APPLIED"] = 1] = "APPLIED";
})(FiltersState || (FiltersState = {}));
var TableAlignment;
(function (TableAlignment) {
    TableAlignment["LEFT"] = "left";
    TableAlignment["CENTER"] = "center";
    TableAlignment["RIGHT"] = "right";
})(TableAlignment || (TableAlignment = {}));

class SearchConfigService {
    constructor(http) {
        this.http = http;
        this._localSearch = ([allData, searchFilters, searchTerm, sort, defaultSort, paging, searchFields, idField, _research,]) => of([
            allData,
            searchFilters,
            searchTerm,
            sort,
            defaultSort,
            paging,
            searchFields,
            idField,
        ]).pipe(first(), map(([allData, searchFilters, searchTerm, sort, defaultSort, paging, searchFields, idField,]) => {
            const dataWithChanges = (idField
                ? allData.map((row) => {
                    var _a;
                    const _idField = idField;
                    const modifiedRow = (_a = this.modifiedData$.value.find((modifiedRow) => modifiedRow[_idField] === row[_idField] &&
                        row[_idField] !== undefined)) !== null && _a !== void 0 ? _a : {};
                    return Object.assign(Object.assign({}, row), modifiedRow);
                })
                : allData);
            return filterer([
                dataWithChanges,
                {
                    page: paging,
                    search: { filters: searchFilters, term: searchTerm },
                    sort: (sort
                        ? Object.keys(sort).length > 0
                            ? sort
                            : defaultSort
                        : defaultSort),
                },
                searchFields,
            ]);
        }), map(sorter), map(paginator), tap((records) => {
            this.data$.next((records === null || records === void 0 ? void 0 : records.records) || []);
            this.totalRecords$.next(records === null || records === void 0 ? void 0 : records.totalRecords);
            this.offPageData$.next((records === null || records === void 0 ? void 0 : records.offPageData) || []);
        }));
        this._onlineSearch = ([allData, searchFilters, searchTerm, sort, defaultSort, paging, searchFields, idField, _research,]) => {
            return this.http
                .fetch({
                endpoint: this.url$.value,
                httpMethod: HttpMethod.POST,
                body: {
                    page: paging,
                    search: { filters: searchFilters, term: searchTerm },
                    sort: (sort
                        ? Object.keys(sort).length > 0
                            ? sort
                            : defaultSort
                        : defaultSort),
                    data: Object.assign({}, this.formData.value),
                },
            })
                .pipe(tap((response) => {
                var _a, _b;
                this.data$.next((response === null || response === void 0 ? void 0 : response.records) || []);
                this.totalRecords$.next((_a = response === null || response === void 0 ? void 0 : response.totalRecords) !== null && _a !== void 0 ? _a : 0);
                this.summaries$.next((_b = response === null || response === void 0 ? void 0 : response.summaries) !== null && _b !== void 0 ? _b : []);
            }));
        };
        this.url$ = new BehaviorSubject('');
        this.status$ = new BehaviorSubject(LoadingState.READY);
        this.labels$ = new BehaviorSubject({});
        this._onDestroy = new Subject();
        this.filters$ = new BehaviorSubject(null);
        this.fieldsDefinition = this.filters$.asObservable().pipe(filter((x) => !!x), withLatestFrom(this.labels$), mapFieldsDefinition());
        this.searchFilters$ = new BehaviorSubject({});
        this.searchTerm$ = new BehaviorSubject('');
        this.toolbarButtons$ = new BehaviorSubject(null);
        this.columnDefinition$ = new BehaviorSubject(null);
        this.columns$ = new BehaviorSubject(null);
        this.defaultSort$ = new BehaviorSubject(null);
        this.sort$ = new BehaviorSubject(null);
        this.totalRecords$ = new BehaviorSubject(0);
        this.paging$ = new BehaviorSubject({
            limit: 9999,
            offset: 0,
        });
        this.allData$ = new BehaviorSubject([]);
        this.modifiedData$ = new BehaviorSubject([]);
        this.data$ = new BehaviorSubject([]);
        this.searchFields$ = new BehaviorSubject([]);
        this.reSearch$ = new Subject();
        this.idField$ = new BehaviorSubject(null);
        this.offPageData$ = new BehaviorSubject([]);
        this.local$ = new BehaviorSubject(true);
        this.summaries$ = new BehaviorSubject([]);
        this.formData = new BehaviorSubject({});
        const columns$ = this.columnDefinition$.pipe(filter((x) => !!x), map(addSelectionColumn), tap((columns) => this.columns$.next(columns)));
        const search$ = combineLatest([
            this.allData$,
            this.searchFilters$,
            this.searchTerm$,
            this.sort$,
            this.defaultSort$,
            this.paging$,
            this.searchFields$,
            this.idField$,
            this.reSearch$.pipe(startWith(undefined)),
        ]).pipe(tap(() => this.status$.next(LoadingState.LOADING)), switchMap((x) => this.local$.value ? this._localSearch(x) : this._onlineSearch(x)), tap(() => this.status$.next(LoadingState.DONE)));
        const searchOffsetReset$ = merge(this.searchTerm$, this.searchFilters$).pipe(
        // Set pagination offset back to 0 when searching or filtering
        tap(() => this.paging$.next(Object.assign(Object.assign({}, this.paging$.value), { offset: 0 }))));
        merge(search$, columns$, searchOffsetReset$)
            .pipe(takeUntil(this._onDestroy))
            .subscribe();
    }
    set url(endpoint) {
        this.url$.next(endpoint);
    }
    get url() {
        return this.url$.value;
    }
    get status() {
        return this.status$.value;
    }
    get labels() {
        return this.labels$.value;
    }
    ngOnDestroy() {
        this._onDestroy.next();
    }
}
SearchConfigService.ɵprov = i0.ɵɵdefineInjectable({ factory: function SearchConfigService_Factory() { return new SearchConfigService(i0.ɵɵinject(BaseHttpService)); }, token: SearchConfigService, providedIn: "root" });
SearchConfigService.decorators = [
    { type: Injectable, args: [{
                providedIn: 'root',
            },] }
];
SearchConfigService.ctorParameters = () => [
    { type: BaseHttpService }
];

class PopupService {
    constructor() {
        this._popups$ = new BehaviorSubject({});
        this.popups$ = this._popups$.pipe(map((popupMap) => Object.values(popupMap)));
    }
    openWizard(config, width = '50vw', overflow = 'scroll') {
        const popup = {
            config,
            width,
            overflow,
            submit$: new Subject(),
            cancel$: new Subject(),
            popupRef: null,
            hasScroll: () => false,
            panelStatus$: new Subject(),
            panelStatuses: new BehaviorSubject({}),
        };
        const popupId = JSON.stringify({
            endpoint: config.endpoint,
            httpMethod: config.httpMethod,
        });
        // Prevent creating multiple popups that target
        // the same endpoint with the same http method.
        // Otherwise form effect could cause infinite loops
        if (this._popups$.value[popupId]) {
            return of();
        }
        this._popups$.next(Object.assign(Object.assign({}, this._popups$.value), { [popupId]: popup }));
        return popup.submit$.asObservable().pipe(first(), map((value) => value), takeUntil(popup.cancel$));
    }
    closeWizard(popup) {
        this._popups$.next(Object.entries(this._popups$.value)
            .filter(([, instance]) => popup !== instance)
            .reduce((obj, [key, val]) => (Object.assign(Object.assign({}, obj), { [key]: val })), {}));
    }
}
PopupService.ɵprov = i0.ɵɵdefineInjectable({ factory: function PopupService_Factory() { return new PopupService(); }, token: PopupService, providedIn: "root" });
PopupService.decorators = [
    { type: Injectable, args: [{
                providedIn: 'root',
            },] }
];
PopupService.ctorParameters = () => [];

var PanelStatus;
(function (PanelStatus) {
    PanelStatus[PanelStatus["CLOSED"] = 0] = "CLOSED";
    PanelStatus[PanelStatus["OPEN"] = 1] = "OPEN";
})(PanelStatus || (PanelStatus = {}));
// can't use a typesafe InjectionToken because Angular legacy view engine is aggressively awful
// and raises ` Function calls are not supported in decorators` errors when injecting it.
// Maybe injection tokens are not allowed to be defined in libraries? No documentation on this
const POPUP_INSTANCE_TOKEN = 'PopupInstance';

/*
  Locks a la mutex
  When a lock is `acquire`d, any further attempts to `acquire` it will fail until it is `release`d.
  If a lock has a `lockedCallback`, that callback will be invoked on every failure.
  
  e.g. a v3wizard acquires the lock "switchMarketplace" with a callback that displays an
  error message. If any other code on the site tries to acquire "switchMarketplace", and the v3wizard
  hasn't released it yet, the callback will be invoked and the error message will be displayed.
*/
class LockHandle {
    constructor(release) {
        this._release = release;
    }
    // e.x. acquire("lockName").then(() => doSomething()).release();
    then(cb) {
        cb();
        return this;
    }
    release() {
        this._release();
    }
}
class LockService {
    constructor() {
        this._locks = new Map();
    }
    acquire(lock, lockedCallback) {
        var _a, _b;
        if (this._locks.has(lock)) {
            (_b = (_a = this._locks.get(lock)).lockedCallback) === null || _b === void 0 ? void 0 : _b.call(_a);
            return null;
        }
        this._locks.set(lock, { lockedCallback });
        return new LockHandle(() => this._locks.delete(lock));
    }
    // Like `acquire` but will never trigger a stored lockedCallback.
    // Use sparingly; you probably want `acquire` instead
    weakAcquire(lock, lockedCallback) {
        return !this.peek(lock) && this.acquire(lock, lockedCallback);
    }
    peek(lock) {
        return this._locks.has(lock);
    }
}
LockService.ɵprov = i0.ɵɵdefineInjectable({ factory: function LockService_Factory() { return new LockService(); }, token: LockService, providedIn: "root" });
LockService.decorators = [
    { type: Injectable, args: [{
                providedIn: 'root',
            },] }
];

var SiteType;
(function (SiteType) {
    SiteType[SiteType["MY_MARKETPLACE"] = 1] = "MY_MARKETPLACE";
    SiteType[SiteType["SUPPLY_CLOUD"] = 2] = "SUPPLY_CLOUD";
})(SiteType || (SiteType = {}));
class SiteTypeService {
    constructor(http) {
        this.http = http;
        this.siteType$ = new BehaviorSubject(undefined);
    }
    setBaseUrl(baseUrl) {
        this.http.baseUrl = baseUrl;
        this.http
            .fetch({
            endpoint: '/site-type',
            httpMethod: 'GET',
        })
            .pipe(tap((res) => this.siteType$.next(res.record)))
            .subscribe();
    }
    get siteType() {
        return this.siteType$.value;
    }
    get siteName() {
        switch (this.siteType) {
            case SiteType.SUPPLY_CLOUD:
                return 'supplycloud';
            default:
                return 'my-marketplace';
        }
    }
    get homeUrl() {
        switch (this.siteType) {
            case SiteType.SUPPLY_CLOUD:
                return 'http://www.gosupplycloud.com';
            default:
                return 'http://www.lbmx.com';
        }
    }
}
SiteTypeService.ɵprov = i0.ɵɵdefineInjectable({ factory: function SiteTypeService_Factory() { return new SiteTypeService(i0.ɵɵinject(BaseHttpService)); }, token: SiteTypeService, providedIn: "root" });
SiteTypeService.decorators = [
    { type: Injectable, args: [{
                providedIn: 'root',
            },] }
];
SiteTypeService.ctorParameters = () => [
    { type: BaseHttpService }
];

/*
 * Public API Surface of root-services
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BaseHttpService, EditLocalSearchService, EditMenuButtonType, FIELD_COMPONENT_PROPERTIES_MAP, FIELD_TYPE_MAP, FieldSetLabel, FieldType, FilterType, FiltersState$2 as FiltersState, HttpMethod, InterceptorResponseService, LoadingState$2 as LoadingState, LockHandle, LockService, POPUP_INSTANCE_TOKEN, PanelStatus, PopupService, SearchConfigService, SearchService, SiteType, SiteTypeService, TableAlignment, addPartialFields, addSelectionColumn, filterPredicates, filterer, getDefaults, mapFieldsDefinition, mapFilters, paginator, schemaDefinition, sorter, urlJoin, ɵ0, ɵ1 };
//# sourceMappingURL=lbmx-root-services.js.map
